import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import FocusLock from 'react-focus-lock';
import { FocusStyles } from '../../../styles/FocusStyles';
import { media, theme } from '../../../components/themes/theme';
import { useMedia } from '../../../hooks/useMedia';
import { OmniButton } from '../../../components/OmniButton';
import { useCloseOnEscape } from '../../../hooks/useCloseOnEscape';
import { Button } from '../../atoms/Button';
import { ColorContextProvider } from '../../providers/ColorContext';
const ViewportWideWrapper = styled.div `
  background: ${props => props.theme.GrayLightest};
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding-bottom: 3rem;
`;
const StyledFocusLock = styled(FocusLock) `
  height: 100%;
  width: 100%;
  overflow-y: auto;
  display: grid;
  grid-template-rows: 3rem auto;
  padding-bottom: 1rem;

  ${media.tablet `
    grid-template-rows: auto auto;
  `}
`;
const BackButton = styled(OmniButton) `
  justify-self: start;
  align-self: end;
  padding: 0;
  margin-left: 1rem;

  svg {
    margin-right: 0;
  }
`;
const LanguageSwitchWrapper = styled.div `
  align-items: center;
  display: grid;
  grid-gap: 5rem;
  grid-template-columns: 1;
  grid-template-rows: auto 1fr auto;
  justify-items: center;
  text-align: center;

  ${media.tablet `
    grid-gap: 3rem;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: none;
    justify-content: center;
  `}
`;
const scalingImage = css `
  max-width: 66%;
  max-height: 43vw;

  ${media.tablet `
    max-width: 100%;
    max-height: none;
    margin-top: 1rem;
  `}
`;
const Orbit = styled.img `
  ${scalingImage}

  ${media.tablet `
    order: 3;
  `};
  ${media.laptop `
    max-width: 99%;
    max-height: none;
  `}
  ${media.laptopL `
    max-width: 69%;
    max-height: none;
  `}
`;
const EarthDoodle = styled.img `
  ${scalingImage}
  ${media.laptop `
    max-width: 80%;
    max-height: none;
    margin-top: 0;
  `}
  ${media.laptopL `
    max-width: 55.5%;
    max-height: none;
  `}
`;
const LanguageList = styled.ul `
  display: grid;
  grid-gap: 3rem;
  list-style: none;
  margin: 0;
  padding: 0;

  ${media.tablet `
    order: 2;
    grid-gap: 2rem;
  `}
`;
const LanguageListItem = styled.li `
  font-size: 1.25rem;
  line-height: 1.2;
  margin: 0;
  padding: 0;
`;
const LanguageSwitchOptionButton = styled.button `
  background: none;
  border: none;
  color: ${props => props.theme.Black};
  font-family: ${props => props.theme.fonts.serif};
  font-size: inherit;
  font-weight: 300;

  ${FocusStyles}

  &:focus,
  &:hover {
    color: ${props => props.theme.BrandPrimary};
    cursor: pointer;
  }

  ${props => props.isActive &&
    css `
      color: ${props => props.theme.BrandPrimary};
      position: relative;

      &::after {
        background-color: ${props => props.theme.BrandPrimary};
        border-radius: 0.125rem;
        bottom: -0.375rem;
        content: '';
        display: block;
        height: 0.125rem;
        left: -1rem;
        position: absolute;
        right: -1rem;
      }
    `}
`;
const ButtonWrapper = styled.span `
  margin: 1rem auto;
`;
export const LanguageSwitch = ({ availableLanguages, className, id = undefined, onSelectLanguage, onClose, shouldFocusFirstElement, }) => {
    const { t } = useTranslation('LanguageSwitch');
    const isLaptopLWidth = useMedia({ minWidth: theme.screenTablet });
    useCloseOnEscape(onClose);
    return (React.createElement(ColorContextProvider, { backgroundColor: "#fff" },
        React.createElement(ViewportWideWrapper, { className: className, "data-testid": "language-switch", id: id },
            React.createElement(StyledFocusLock, { autoFocus: shouldFocusFirstElement, returnFocus: true },
                !isLaptopLWidth && (React.createElement(BackButton, { isBackLink: true, onClick: onClose, dataTestId: "language-switch-button-back" }, t('backMobile'))),
                React.createElement(LanguageSwitchWrapper, null,
                    React.createElement(Orbit, { "data-testid": "language-switch-orbit", src: "https://cdn.tonies.de/doodles/doodle_hello.svg", alt: "Orbit" }),
                    React.createElement(LanguageList, { "data-testid": "language-switch-language-list" }, availableLanguages.map(language => (React.createElement(LanguageListItem, { key: language.languageCode },
                        React.createElement(LanguageSwitchOptionButton, { "data-testid": "language-switch-option-button", isActive: language.isActive, onClick: () => onSelectLanguage(language.languageCode) }, language.text))))),
                    isLaptopLWidth ? (React.createElement(EarthDoodle, { "data-testid": "language-switch-earth-with-bubbles", src: "https://cdn.tonies.de/doodles/doodle_rocket.svg", alt: "world" })) : (React.createElement(EarthDoodle, { "data-testid": "language-switch-earth", src: "https://cdn.tonies.de/doodles/doodle_rocket_mobile.svg", alt: "world" }))),
                isLaptopLWidth && (React.createElement(ButtonWrapper, null,
                    React.createElement(Button, { variant: "secondary", onClick: onClose, "data-testid": "language-switch-button-cancel" }, t('backDesktop'))))))));
};
